import { notificationService } from '../services/notificationService';
import type { Notification } from '../services/notificationService';

/**
 * Demo notification generator for testing the notification system
 * Access via browser console: window.demoNotifications
 */
export class DemoNotifications {
  private userId: string;

  constructor(userId: string) {
    this.userId = userId;
  }

  /**
   * Generate all types of notifications for testing
   */
  generateAll() {
    console.log('🔔 Generating demo notifications...');
    
    this.pointsExpiry();
    setTimeout(() => this.dealAlert(), 500);
    setTimeout(() => this.transaction(), 1000);
    setTimeout(() => this.transferOpportunity(), 1500);
    setTimeout(() => this.milestone(), 2000);
    setTimeout(() => this.priceDrop(), 2500);
    setTimeout(() => this.lowBalance(), 3000);
    setTimeout(() => this.syncSuccess(), 3500);
    
    console.log('✅ Demo notifications generated!');
  }

  /**
   * Generate points expiry notification
   */
  pointsExpiry() {
    notificationService.addNotification(this.userId, {
      type: 'points_expiry',
      priority: 'urgent',
      title: 'Points Expiring Soon!',
      message: '25,000 Chase Ultimate Rewards points expire in 15 days',
      actionUrl: '/dashboard',
      actionLabel: 'View Details',
      metadata: {
        programName: 'Chase Ultimate Rewards',
        points: 25000,
        expiryDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
      },
      dismissible: true,
      persistent: true,
    });
    console.log('✓ Points expiry notification added');
  }

  /**
   * Generate deal alert notification
   */
  dealAlert() {
    notificationService.addNotification(this.userId, {
      type: 'deal_alert',
      priority: 'high',
      title: '🔥 30% Transfer Bonus!',
      message: 'Transfer Chase UR to Hyatt and get 30% bonus points',
      actionUrl: '/deals',
      actionLabel: 'View Deal',
      dismissible: true,
      persistent: true,
    });
    console.log('✓ Deal alert notification added');
  }

  /**
   * Generate transaction notification
   */
  transaction() {
    notificationService.addNotification(this.userId, {
      type: 'transaction_added',
      priority: 'low',
      title: 'Points Earned',
      message: '5,000 points added to American Express Membership Rewards',
      actionUrl: '/cards',
      actionLabel: 'View Details',
      metadata: {
        programName: 'American Express MR',
        points: 5000,
      },
      dismissible: true,
    });
    console.log('✓ Transaction notification added');
  }

  /**
   * Generate transfer opportunity notification
   */
  transferOpportunity() {
    notificationService.addNotification(this.userId, {
      type: 'transfer_opportunity',
      priority: 'high',
      title: '⚡ Transfer Bonus Available!',
      message: 'Transfer from Amex to Virgin Atlantic and get 40% bonus points (limited time)',
      actionUrl: '/airlines',
      actionLabel: 'View Programs',
      metadata: {
        fromProgram: 'American Express MR',
        toProgram: 'Virgin Atlantic',
        bonusPercentage: 40,
      },
      dismissible: true,
      persistent: true,
    });
    console.log('✓ Transfer opportunity notification added');
  }

  /**
   * Generate milestone notification
   */
  milestone() {
    notificationService.addNotification(this.userId, {
      type: 'milestone_reached',
      priority: 'medium',
      title: '🎉 Milestone Reached!',
      message: "You've reached 100,000 points in Chase Sapphire Reserve! Current balance: 103,450",
      actionUrl: '/cards',
      actionLabel: 'View Program',
      metadata: {
        programName: 'Chase Sapphire Reserve',
        milestone: 100000,
        points: 103450,
      },
      dismissible: true,
      persistent: true,
    });
    console.log('✓ Milestone notification added');
  }

  /**
   * Generate price drop notification
   */
  priceDrop() {
    notificationService.addNotification(this.userId, {
      type: 'price_drop',
      priority: 'high',
      title: '✈️ Price Drop Alert!',
      message: 'JFK → Tokyo dropped 35% to $649 (Economy)',
      actionUrl: '/deals',
      actionLabel: 'Book Now',
      metadata: {
        route: 'JFK → Tokyo',
        oldPrice: 999,
        newPrice: 649,
        percentageDrop: 35,
      },
      dismissible: true,
      persistent: true,
    });
    console.log('✓ Price drop notification added');
  }

  /**
   * Generate low balance notification
   */
  lowBalance() {
    notificationService.addNotification(this.userId, {
      type: 'low_balance',
      priority: 'low',
      title: 'Low Points Balance',
      message: 'Your Hilton Honors balance (3,200) is below 5,000 points',
      actionUrl: '/cards',
      actionLabel: 'View Program',
      metadata: {
        programName: 'Hilton Honors',
        points: 3200,
        threshold: 5000,
      },
      dismissible: true,
    });
    console.log('✓ Low balance notification added');
  }

  /**
   * Generate sync success notification
   */
  syncSuccess() {
    notificationService.addNotification(this.userId, {
      type: 'sync_success',
      priority: 'low',
      title: 'Sync Complete',
      message: 'Successfully synced 3 programs and 12 transactions',
      dismissible: true,
    });
    console.log('✓ Sync success notification added');
  }

  /**
   * Generate bonus opportunity notification
   */
  bonusOpportunity() {
    notificationService.addNotification(this.userId, {
      type: 'bonus_opportunity',
      priority: 'medium',
      title: '💰 Limited Time Offer',
      message: 'Earn 5X points on dining this week with Chase Sapphire Preferred',
      actionUrl: '/cards',
      actionLabel: 'Learn More',
      dismissible: true,
      persistent: true,
    });
    console.log('✓ Bonus opportunity notification added');
  }

  /**
   * Generate redemption suggestion notification
   */
  redemptionSuggestion() {
    notificationService.addNotification(this.userId, {
      type: 'redemption_suggestion',
      priority: 'medium',
      title: '💡 Smart Redemption Tip',
      message: 'Transfer your 50k Chase UR points to Hyatt for a $700+ hotel stay (1.4cpp value)',
      actionUrl: '/hotels',
      actionLabel: 'View Hotels',
      metadata: {
        value: 700,
      },
      dismissible: true,
      persistent: true,
    });
    console.log('✓ Redemption suggestion notification added');
  }

  /**
   * Clear all notifications
   */
  clearAll() {
    notificationService.clearAll(this.userId);
    console.log('🗑️ All notifications cleared');
  }

  /**
   * Get current notification count
   */
  async getCount() {
    const count = await notificationService.getUnreadCount(this.userId);
    console.log(`📊 You have ${count} unread notifications`);
    return count;
  }

  /**
   * List all notifications
   */
  async listAll() {
    const notifications = await notificationService.getNotifications(this.userId);
    console.log(`📋 Total notifications: ${notifications.length}`);
    console.table(notifications.map((n: Notification) => ({
      type: n.type,
      priority: n.priority,
      title: n.title,
      read: n.read,
      timestamp: n.timestamp.toLocaleString(),
    })));
    return notifications;
  }
}

// Make it available globally for testing
if (typeof window !== 'undefined') {
  (window as any).DemoNotifications = DemoNotifications;
  (window as any).notificationService = notificationService;
  console.log('🔔 Demo notifications available!');
  console.log('Usage: new DemoNotifications(userId).generateAll()');
  console.log('Or use notificationService directly: notificationService.addNotification(userId, {...})');
}
